import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Technician } from '../../types';
import { apiClient } from '../../services/apiClient';
import './SelectionModal.css';

interface TechnicianSelectionModalProps {
  onSelect: (technician: Technician) => void;
  onClose: () => void;
}

const DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 2;

const TechnicianSelectionModal: React.FC<TechnicianSelectionModalProps> = ({ onSelect, onClose }) => {
  const { t } = useTranslation();
  const [technicians, setTechnicians] = useState<Technician[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const getDisplayName = (technician: Technician): string => {
    if (technician.name) {
      return technician.name;
    }
    const parts = [technician.firstName, technician.lastName].filter(Boolean);
    return parts.length > 0 ? parts.join(' ') : technician.email;
  };

  const searchTechnicians = useCallback(async (query: string) => {
    if (query.length < MIN_SEARCH_LENGTH) {
      setTechnicians([]);
      setHasSearched(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await apiClient.searchTechnicians(query);
      setTechnicians(data);
      setHasSearched(true);
    } catch (err) {
      setError(t('messages.errorLoadingData'));
      setTechnicians([]);
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    // Clear previous timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer for debounced search
    debounceTimerRef.current = setTimeout(() => {
      searchTechnicians(searchQuery);
    }, DEBOUNCE_MS);

    // Cleanup on unmount or query change
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery, searchTechnicians]);

  const handleSelect = (technician: Technician) => {
    onSelect(technician);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{t('reportForm.selectTechnician')}</h3>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="modal-body">
          <input
            type="text"
            placeholder={t('common.search') + '...'}
            value={searchQuery}
            onChange={handleSearchChange}
            className="filter-input"
            autoFocus
          />

          <div className="search-results-container">
            {searchQuery.length > 0 && searchQuery.length < MIN_SEARCH_LENGTH && (
              <div className="search-hint">
                {t('reportForm.minSearchCharsTechnician', { count: MIN_SEARCH_LENGTH })}
              </div>
            )}

            {loading && <div className="loading">{t('app.loading')}</div>}

            {error && <div className="error-message">{error}</div>}

            {!loading && !error && searchQuery.length === 0 && (
              <div className="search-hint">
                {t('reportForm.typeToSearchTechnician')}
              </div>
            )}

            {!loading && !error && hasSearched && (
              <div className="selection-list">
                {technicians.map((technician) => (
                  <div
                    key={technician.id}
                    className="selection-item"
                    onClick={() => handleSelect(technician)}
                  >
                    <div className="item-main">{getDisplayName(technician)}</div>
                    {technician.email && <div className="item-details">{technician.email}</div>}
                  </div>
                ))}
                {technicians.length === 0 && (
                  <div className="no-results">{t('mainForm.noResults')}</div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicianSelectionModal;
